import { COMMENTS_PUBSUB_EVENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { getExperienceTracker } from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';
import { getProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { isCommentResolved } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import type { fetchCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';

/** Which handler produced this experience event. Some inline events are handled by both editor and renderer */
export enum CommentsPubSubSource {
	EditorInlineCommentsPubSub = 'editorInlineCommentsPubSub',
	RendererInlineCommentsPubSub = 'rendererInlineCommentsPubSub',
	PageCommentsPubSub = 'pageCommentsPubSub',
}

/**
 * Which staleness check a refetched comment needs, based on the event that triggered the refetch.
 */
export enum CommentsPubsubTrackedEventType {
	Resolve = 'resolve',
	Reopen = 'reopen',
	Edit = 'edit',
}

type FetchedComment = Awaited<ReturnType<typeof fetchCommentById>>;

export type TrackedCommentEvent = {
	/** The refetched comment to validate - a value or a promise of one, both are awaited. */
	comment: FetchedComment | Promise<FetchedComment>;
	type: CommentsPubsubTrackedEventType;
	previousVersionNumber?: number | null;
};

export type CommentsPubSubExperienceTracker = {
	start: (event: string) => void;
	fail: (err: unknown) => void;
	/**
	 * The terminal call for an event. Awaits everything it is given, validates each refetched
	 * comment against the event that triggered it, then either fails once (stale read) or succeeds
	 * once. Plain promises are just awaited - there is nothing to validate. Call it with no
	 * arguments when there is nothing to await or validate.
	 */
	handleCompletePubSubExperiences: (
		eventsToValidate?: (TrackedCommentEvent | Promise<void>)[],
	) => Promise<void>;
};

/**
 * A commentResolved / InlineCommentResolved event promises the refetched comment is resolved.
 * If a network-only refetch still shows it unresolved, the read raced ahead of replication and
 * the success can't be verified.
 */
const getStaleResolveError = (comment: FetchedComment): Error | undefined =>
	comment && isCommentResolved(comment) ? undefined : new Error('commentResolved: stale read');

/**
 * A commentReopened / InlineCommentUnresolved event promises the refetched comment is
 * unresolved. Same staleness risk as resolve, in the other direction.
 */
const getStaleReopenError = (comment: FetchedComment): Error | undefined =>
	comment && !isCommentResolved(comment) ? undefined : new Error('commentReopened: stale read');

/**
 * A commentUpdated / InlineCommentUpdated event carries no version number of its own (neither
 * the AVI nor the AGG payload includes one), so the only way to catch a stale (pre-edit) read is
 * comparing the refetched version against the version we had cached before the refetch was
 * issued (see getCachedCommentVersionNumber).
 */
const getStaleEditError = (
	comment: FetchedComment,
	previousVersionNumber: number | null | undefined,
): Error | undefined => {
	const currentVersionNumber = comment?.version?.number ?? null;
	return previousVersionNumber != null &&
		currentVersionNumber != null &&
		currentVersionNumber <= previousVersionNumber
		? new Error('commentUpdated: stale read')
		: undefined;
};

const getStaleCommentError = (
	type: CommentsPubsubTrackedEventType,
	comment: FetchedComment,
	previousVersionNumber: number | null | undefined,
): Error | undefined => {
	switch (type) {
		case CommentsPubsubTrackedEventType.Resolve:
			return getStaleResolveError(comment);
		case CommentsPubsubTrackedEventType.Reopen:
			return getStaleReopenError(comment);
		case CommentsPubsubTrackedEventType.Edit:
			return getStaleEditError(comment, previousVersionNumber);
		default:
			return undefined;
	}
};

export const createCommentsPubSubExperienceTracker = (
	source: CommentsPubSubSource,
	commentId: string | null | undefined,
): CommentsPubSubExperienceTracker => {
	// Unique per event invocation; lets us detect concurrent-event collisions
	// by matching initialTrackingId === terminalTrackingId in logs.
	let trackingId: string | undefined;

	const fail = (err: unknown) => {
		getExperienceTracker().fail({
			name: COMMENTS_PUBSUB_EVENT_EXPERIENCE,
			error: err instanceof Error ? err : new Error(String(err)),
			attributes: { terminalTrackingId: trackingId },
		});
	};

	return {
		start: (event) => {
			// IMPORTANT - because this is in a factory method, it is deliberate that mixing
			// & matching starts / failures / successes across invocations will lead to different trackingIDs.
			// If you need to refactor this to be cross-file, or cross-function for a valid case,
			// you'll probably need to lift this out of the factory
			trackingId = crypto.randomUUID();
			getExperienceTracker().start({
				name: COMMENTS_PUBSUB_EVENT_EXPERIENCE,
				// 15s — above the AGG client's ~10s network timeout.
				timeout: 15000,
				attributes: {
					event,
					source,
					initialTrackingId: trackingId,
					commentId,
				},
			});
		},
		fail,
		handleCompletePubSubExperiences: async (eventsToValidate = []) => {
			// Rejections deliberately propagate rather than being reported here - a failing
			// fetchCommentById is handled by the caller's existing catch, which fails the
			// experience and rethrows.
			const staleErrors = await Promise.all(
				eventsToValidate.map(async (trackedEvent) => {
					// Each entry is either a plain Promise<void> or a TrackedCommentEvent - never
					// both - so only one of these two awaits is ever doing real work. Awaiting a
					// plain promise resolves to undefined and short-circuits below. Awaiting a
					// TrackedCommentEvent (not itself a promise) resolves immediately to itself;
					// its `comment` field is what actually gets awaited next.
					const event = await trackedEvent;
					if (!event) {
						return undefined;
					}

					return getStaleCommentError(event.type, await event.comment, event.previousVersionNumber);
				}),
			);

			const staleError = staleErrors.find((error) => error);

			if (staleError) {
				fail(staleError);
				return;
			}

			getExperienceTracker().succeed({
				name: COMMENTS_PUBSUB_EVENT_EXPERIENCE,
				attributes: { terminalTrackingId: trackingId },
			});
		},
	};
};

/**
 * Snapshot of a comment's version number as currently held in the comments data cache. Read
 * this before issuing a network-only refetch for an edit event, so the edit staleness check has
 * a "previous" version to compare the refetched ("current") version against.
 */
export const getCachedCommentVersionNumber = (commentId: string): number | null =>
	getProcessedCommentsDataState().flatCommentsMap[commentId]?.version?.number ?? null;
